/**
 * LoginPage - The authentication entry point for the GBRANDS application.
 *
 * Displays a centered login form with three authentication methods:
 *	 1. Email/password (standard credentials submitted to the API)
 *	 2. Office 365 (Azure AD) - redirects to the backend OAuth flow
 *	 3. Google OAuth - redirects to the backend OAuth flow
 *
 * On successful login the user is redirected to the dashboard ("/").
 * This page is the only publicly accessible route; all other routes
 * require authentication via <ProtectedRoute>.
 *
 * Accessible by: unauthenticated users only (authenticated users should
 * be redirected away by the router before reaching this page).
 */
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { Alert, BrandWordmark, Button } from '../../components/atoms';
import { getReleaseStamp } from '../../utils/releaseStamp';

const LoginPage: React.FC = () => {
	// --- Form state ---
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	// Controls whether the password field shows plain text or dots
	const [showPassword, setShowPassword] = useState(false);

	// --- Feedback state ---
	// Holds the error message shown in the red banner when login fails
	const [error, setError] = useState<string | null>(null);
	// Prevents duplicate submissions while the login request is in-flight
	const [isSubmitting, setIsSubmitting] = useState(false);

	// --- Auth & navigation ---
	// Destructure the three login methods from the auth context
	const { login, loginWithO365, loginWithGoogle } = useAuth();
	const navigate = useNavigate();
	const location = useLocation();

	// Build identity stamped onto <html> by scripts/with-release.js. Null in
	// tests and in any build that bypassed the wrapper — the footer is simply
	// omitted then rather than rendering a half-empty line.
	const releaseStamp = getReleaseStamp();

	useEffect(() => {
		const oauthError = (location.state as { oauthError?: string } | null)?.oauthError;
		if (oauthError) {
			setError(oauthError);
			navigate(location.pathname, { replace: true, state: null });
		}
	}, [location.pathname, location.state, navigate]);

	/**
	 * handleSubmit - Handles the email/password form submission.
	 * Calls the login() function from AuthContext, stores tokens via the
	 * auth service, then navigates to the dashboard on success.
	 * If the user must change their password, redirects to /change-password instead.
	 * On failure, displays the error message returned by the API.
	 */
	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError(null);
		setIsSubmitting(true);
		try {
			await login(email, password);
			// login() sets the user in AuthContext — we can't read the updated
			// user here synchronously, so we'll let ProtectedRoute handle the
			// redirect via the mustChangePassword check. Navigate to "/" and
			// ProtectedRoute will redirect to /change-password if needed.
			navigate('/');
		} catch (err) {
			// Surface a human-readable message; fall back to generic text
			setError(err instanceof Error ? err.message : 'Login failed');
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		// flex-col + a flex-1 centring row, rather than centring the whole page:
		// it keeps the card in the middle of the space *above* the footer instead
		// of letting the footer shove it upward.
		<div className="login-page min-h-screen min-h-[100dvh] flex flex-col bg-[#eae5e5] p-16px" data-ref="page-login-page">
			<div className="login-page__body flex-1 flex items-center justify-center">
				<div className="max-w-md w-full space-y-32px">
					{/* --- Branding header --- */}
					<div className="text-center">
						<BrandWordmark as="h1" size="xl" className="login-page__brand font-bold" />
						<p className="mt-8px text-14px font-sans text-brand-text-muted">Work Order &amp; Inspection Management</p>
					</div>

					{/* --- Login card --- */}
					<div className="bg-white py-32px px-24px shadow-lg rounded-lg">
						{/* Conditional error banner - only renders when login has failed */}
						{error && (
							<Alert variant="error" className="mb-16px">{error}</Alert>
						)}

						{/* --- Email / password form --- */}
						<form onSubmit={handleSubmit} className="space-y-24px">
							<div>
								<label htmlFor="email" className="block text-14px font-medium font-sans text-brand-text">Email address</label>
								<input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-4px block w-full rounded-md border border-gray-300 px-12px py-8px shadow-sm font-sans focus:border-brand-primary focus:outline-none focus:ring-1 focus:ring-brand-primary" placeholder="you@example.com" />
							</div>

							<div>
								<label htmlFor="password" className="block text-14px font-medium font-sans text-brand-text">Password</label>
								<div className="relative mt-4px">
									<input id="password" type={showPassword ? 'text' : 'password'} required value={password} onChange={(e) => setPassword(e.target.value)} className="block w-full rounded-md border border-gray-300 px-12px py-8px pr-44px shadow-sm font-sans focus:border-brand-primary focus:outline-none focus:ring-1 focus:ring-brand-primary" placeholder="Enter your password" />
									{/* Password visibility toggle button */}
									<button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 flex items-center pr-12px text-gray-400 hover:text-gray-600" aria-label={showPassword ? 'Hide password' : 'Show password'}>
										{showPassword ? (
											<svg xmlns="http://www.w3.org/2000/svg" className="h-20px w-20px" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L6.59 6.59m7.532 7.532l3.29 3.29M3 3l18 18" /></svg>
										) : (
											<svg xmlns="http://www.w3.org/2000/svg" className="h-20px w-20px" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
										)}
									</button>
								</div>
							</div>

							{/* Submit button - disabled while the request is in-flight */}
							<Button type="submit" variant="primary" size="sm" fullWidth disabled={isSubmitting} className="shadow-sm">
								{isSubmitting ? 'Signing in...' : 'Sign in'}
							</Button>
						</form>

						{/* --- OAuth provider divider and buttons --- */}
						<div className="mt-24px">
							{/* Visual divider with "Or continue with" label */}
							<div className="relative">
								<div className="absolute inset-0 flex items-center">
									<div className="w-full border-t border-gray-300" />
								</div>
								<div className="relative flex justify-center text-14px">
									<span className="px-8px bg-white text-brand-text-subtle font-sans">Or continue with</span>
								</div>
							</div>

							{/* OAuth buttons - each triggers a full-page redirect to the backend OAuth endpoint */}
							<div className="mt-24px grid grid-cols-2 gap-12px">
								<button type="button" onClick={loginWithO365} className="w-full inline-flex items-center justify-center py-8px px-16px min-h-44px border border-gray-300 rounded-md shadow-sm bg-white text-14px font-medium font-sans text-brand-text hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary">Office 365</button>
								<button type="button" onClick={loginWithGoogle} className="w-full inline-flex items-center justify-center py-8px px-16px min-h-44px border border-gray-300 rounded-md shadow-sm bg-white text-14px font-medium font-sans text-brand-text hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary">Google</button>
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* --- Build stamp ---
			     Which bundle is this browser actually running? Deliberately tiny and
			     faded: it is diagnostic text for a support call, not something a user
			     signing in should have to read past. Omitted entirely when unstamped
			     (see getReleaseStamp) rather than shown as a blank line. */}
			{releaseStamp && (
				<footer className="login-page__footer pt-16px text-center font-sans text-8px text-brand-text-muted opacity-50">
					{releaseStamp.release} &middot; {releaseStamp.timestamp}
				</footer>
			)}
		</div>
	);
};

export default LoginPage;
